/************************************************************************************
 * [COMPONENT]: L-HEADER
 * Controls main header functionality including cart interactions
 *************************************************************************************/

class LTAHeader extends HTMLElement {
	constructor() {
		super()

		this.cartLinkEls = []
	}

	connectedCallback() {
		this.cacheElements()
		this.addEventListeners()
	}

	cacheElements() {
		this.cartLinkEls = Array.from(this.querySelectorAll('[data-cart-link]'))
	}

	addEventListeners() {
		this.initStickyMode()
	}

	initStickyMode() {
		window.addEventListener('scroll', () => {
			const stickyThreshold = this.parentElement?.offsetHeight || 0
			this.parentElement?.classList.toggle('is-sticky-mode', window.scrollY > stickyThreshold)
		})
	}
}

customElements.define('l-header', LTAHeader)

/************************************************************************************
 * [COMPONENT]: L-HEADER-INLINE-MENU
 *************************************************************************************/

if (!customElements.get('l-header-inline-menu')) {
	class LTAHeaderInline extends HTMLElement {
		constructor() {
			super()

			this.ANIMATION_DURATION = 400
			this._handleSummaryClick = this._handleSummaryClick.bind(this)
			this._handleDocumentClick = this._handleDocumentClick.bind(this)
			this._activeTrap = null // keep track of which element has trapFocus
		}

		connectedCallback() {
			this.detailsEls = Array.from(this.querySelectorAll('details'))

			// Attach listeners to all summaries
			this.detailsEls.forEach(async (detailsEl) => {
				const summary = detailsEl.querySelector('summary')
				if (summary) {
					summary.addEventListener('click', this._handleSummaryClick.bind(this, detailsEl))
					detailsEl.addEventListener('mouseenter', this._handleSummaryClick.bind(this, detailsEl))
					detailsEl.addEventListener(
            'mouseleave',
						l_debounce(() => this._closeWithAnimation(detailsEl), 100)
					)
				}
			})

			document.addEventListener('click', this._handleDocumentClick)
		}

		disconnectedCallback() {
			this.detailsEls?.forEach((detailsEl) => {
				const summary = detailsEl.querySelector('summary')
				if (summary) summary.removeEventListener('click', this._handleSummaryClick)
			})

			document.removeEventListener('click', this._handleDocumentClick)
		}

		async _handleSummaryClick(toggledEl, event) {
			event.preventDefault()

			const level = toggledEl.dataset.level
			const openEls = this.detailsEls.filter((el) => el.dataset.level === level && el !== toggledEl && el.open)

			this._openWithAnimation(toggledEl)

			// Close all other open siblings first
			openEls.map((el) => this._closeWithAnimation(el))
		}

		_openWithAnimation(detailsEl) {
			const content = detailsEl.querySelector(':scope > *:not(summary)')
			if (!content) return

			const openingMenuEl = this.querySelector('details[open]')

			detailsEl.classList.add('is-opening')
			detailsEl.setAttribute('open', '')

			if (!openingMenuEl) {
				content.style.height = '0px'
			}

			content.style.overflow = 'hidden'
			content.style.transition = 'height 0.6s ease'

			requestAnimationFrame(() => {
				content.style.height = `${content.scrollHeight}px`

				if (openingMenuEl) {
					content.style.opacity = '0'
					content.style.transition = 'opacity 0.6s ease'

					requestAnimationFrame(() => {
						content.style.opacity = '1'
					})
				}
			})

			// Lock body scroll if the details element has a mega menu
			const hasMegaMenu = detailsEl.querySelector('[data-mega-menu]')
			if (hasMegaMenu) {
				l_lockBodyScroll('header-inline-menu')
			}
		}

		_closeWithAnimation(detailsEl) {
			const content = detailsEl.querySelector(':scope > *:not(summary)')
			if (!content) return

			// 🔁 Close nested details recursively
			const nestedDetails = Array.from(detailsEl.querySelectorAll(':scope details[open]'))
			nestedDetails.map((child) => this._closeWithAnimation(child))

			const startHeight = content.scrollHeight

			detailsEl.classList.remove('is-opening')
			content.style.height = `${startHeight}px`
			content.style.overflow = 'hidden'
			content.style.transition = 'height 0.6s ease'

			requestAnimationFrame(() => {
				content.style.height = '0px'
			})

			// Unlock body scroll if the details element has a mega menu
			const hasMegaMenu = detailsEl.querySelector('[data-mega-menu]')
			if (hasMegaMenu) {
				l_unLockBodyScroll('header-inline-menu')
			}

			l_debounce(() => {
				detailsEl.removeAttribute('open')

				content.style.height = ''
				content.style.overflow = ''
				content.style.transition = ''
			}, this.ANIMATION_DURATION)()
		}

		_handleDocumentClick(event) {
			if (this.contains(event.target)) return // ignore inside clicks

			const openEls = this.detailsEls.filter((el) => el.open)
			openEls.forEach((el) => this._closeWithAnimation(el))

			if (openEls.length > 0) {
				// clear trap focus globally
				l_removeTrapFocus()
				this._activeTrap = null
			}
		}
	}

	customElements.define('l-header-inline-menu', LTAHeaderInline)
}

/************************************************************************************
 * [MODULE]: MOBILE MENU DRAWERS
 * Controls mobile menu drawers functionality
 *************************************************************************************/

class LTAMainMenuDrawer extends LTADrawer {
	// To keep the focus on the trigger element based on the race condition
	drawer_close() {
		super.drawer_close()
	}
}

customElements.define('l-main-menu-drawer', LTAMainMenuDrawer)

/************************************************************************************
 * [MODULE]: SEARCH / PREDICTIVE SEARCH MODULE
 *************************************************************************************/

class LTASearchDrawer extends LTADrawer {
	connectedCallback() {
		super.connectedCallback()
		this._cacheElements()
		this._initCache()
		this._addEventListeners()
	}

	_cacheElements() {
		this.searchInputEl = this.querySelector('[data-search-input]')
		this.searchButtonEl = this.querySelector('[data-search-button]')
		this.searchResultsEl = this.querySelector('[data-results]')
	}

	_initCache() {
		this.cachedResults = {}
	}

	_addEventListeners() {
		const debouncedInput = l_debounce(this.handleSearchInput.bind(this), 300)
		this.searchInputEl.addEventListener('input', debouncedInput)
	}

	handleSearchInput() {
		const searchTerm = this._normalizeSearchTerm(this.searchInputEl.value)

		if (!searchTerm) {
			this._toggleSearchResults(false)
			return
		}

		this._toggleSearchResults(true)
		this._setLoading(true)

		if (this.cachedResults[searchTerm]) {
			this._renderSearchResults(this.cachedResults[searchTerm])
			return
		}

		this._fetchSearchResults(searchTerm)
	}

	_normalizeSearchTerm(term) {
		if (!term) return ''
		return term.trim().replace(/ /g, '-').toLowerCase()
	}

	_fetchSearchResults(searchTerm) {
		const baseURL = window.LTATheme.routes.predictive_search_url
		const params = `?q=${encodeURIComponent(searchTerm)}&section_id=a-ajax-header-predictive-search`
		const searchURL = `${baseURL}${params}`

		fetch(searchURL)
			.then((response) => response.text())
			.then((htmlText) => {
				const resultsMarkup = l_getSectionDOM(htmlText, '#shopify-section-a-ajax-header-predictive-search')
				this.cachedResults[searchTerm] = resultsMarkup
				l_debounce(() => this._renderSearchResults(resultsMarkup), 100)()
			})
			.catch(console.error)
			.finally(() => this._setLoading(false))
	}

	_renderSearchResults(resultsMarkup) {
		this.searchResultsEl.innerHTML = resultsMarkup.innerHTML
		l_trapFocus(this, this.searchInputEl, false)
		this._setLoading(false)
	}

	_setLoading(isLoading) {
		this.searchButtonEl.classList.toggle('l-button-loading', isLoading)
	}

	_toggleSearchResults(show) {
		if (show) {
			this.searchResultsEl.classList.remove('l-hidden')
			this.searchResultsEl.removeAttribute('hidden')
			this.searchResultsEl.removeAttribute('tabindex')
		} else {
			this.searchResultsEl.classList.add('l-hidden')
			this.searchResultsEl.setAttribute('hidden', '')
			this.searchResultsEl.setAttribute('tabindex', '-1')
		}
	}
}

customElements.define('l-search-drawer', LTASearchDrawer)

